import type { FirstTierSupplier, part, partSKP } from "@/api/supplierService";
import Breadcrumb from "@/components/Breadcrumb";
import BackButton from "@/components/buttons/BackButton";
import SupplierDetailCard from "@/components/first-tier-supplier/SupplierDetailCard";
import {
  useGetFirstTierSuppliers,
  useGetPartBySupplier,
  useImportPart,
  useImportPartV1,
  useInsertPartToSupplier,
  useUpdatePart,
} from "@/hooks/supplierHooks";
import { formattedDateTime } from "@/utils/date";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PartManagementTable from "./PartManagementTable";
import { Add, Info, InsertDriveFile, Upload } from "@mui/icons-material";
import { DefaultSecondaryButton } from "@/styles/globalstyles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import CheckIcon from "@mui/icons-material/Check";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import SavePartModal from "@/components/first-tier-supplier/SavePartModal";
import DuplicatePartModal from "@/components/first-tier-supplier/DuplicatePartModal";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import { ConfirmAlert, ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";
import { useUserInfo } from "@/context/UserInfoContext";
import { useFlagPartHandler } from "@/hooks/useFlagPartHandler";
import UnFlaggedModal from "@/components/flagged-part/UnFlaggedModal";
import ImportPartModal from "@/components/first-tier-supplier/ImportPartModal";
import ImportPreviewModal from "@/components/first-tier-supplier/ImportPreviewModal";
import ImportPreviewModalV1 from "@/components/first-tier-supplier/ImportPreviewModalV1";
import {
  exportPartTemplateToExcel,
  parsePartFromExcel,
  parsePartV1FromExcel,
} from "@/utils/excel/index";
import type { ParsedImportResult, ParsedImportV1Result } from "@/utils/excel";
import { exportAllPartToExcel } from "@/utils/excel/exportAllPart";
import { exportPartV1TemplateToExcel, exportAllPartV1ToExcel } from "@/utils/excel/exportPart";

const PartManagement = () => {
  const navigate = useNavigate();
  const canEdit = usePermissions().canEdit("breakdown");
  const { showToast } = useToast();
  const [supplier, setSupplier] = useState<FirstTierSupplier | null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [isOpenUnFlagModal, setIsOpenUnFlagModal] = useState<boolean>(false);
  const [flagPartId, setFlagPartId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo(
    () => Number(searchParams.get("active")) || 0,
    [searchParams],
  );
  const page = useMemo(
    () => Number(searchParams.get("page")) || 0,
    [searchParams],
  );
  const rowsPerPage = useMemo(
    () => Number(searchParams.get("limit")) || 10,
    [searchParams],
  );
  const version = useMemo(
    () => (searchParams.get("version") === "v1" ? "v1" : "v2"),
    [searchParams],
  );
  const [selectedParts, setSelectedParts] = useState<part[]>([]);
  const [openSavePartModal, setOpenSavePartModal] = useState<boolean>(false);
  const [openDuplicatePartModal, setOpenDuplicatePartModal] =
    useState<boolean>(false);
  const [openImportModal, setOpenImportModal] = useState<boolean>(false);
  const [parsedImportResult, setParsedImportResult] =
    useState<ParsedImportResult | null>(null);
  const [openImportV1Modal, setOpenImportV1Modal] = useState<boolean>(false);
  const [parsedImportV1Result, setParsedImportV1Result] =
    useState<ParsedImportV1Result | null>(null);
  const [isFetchingForDownload, setIsFetchingForDownload] =
    useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const { supplierCode } = useParams<{ supplierCode: string }>();
  const userInfo = useUserInfo();
  const canImportV1 = userInfo?.groups?.some(
    (g) => g.toLowerCase() === "developer",
  );

  const {
    data: firstTierSuppliers,
    isLoading: isLoadingFirstTierSuppliers,
    isError: isErrorFirstTierSuppliers,
  } = useGetFirstTierSuppliers({
    skip: 0,
    limit: 2000,
    buyer_code: userInfo?.buyer_code,
  });

  const {
    data: parts,
    isLoading: isLoadingParts,
    isError: isErrorParts,
  } = useGetPartBySupplier(
    supplier ? supplier.supplier_code : "",
    version,
    activeTab === 0,
  );

  const { mutate: insertPart, isPending: isInsertingPart } =
    useInsertPartToSupplier();

  const { mutate: updatePart, isPending: isUpdatingPart } = useUpdatePart();

  const { mutate: importPart, isPending: isImportSubmitting } = useImportPart();
  const { mutate: importPartV1, isPending: isImportV1Submitting } = useImportPartV1();

  const { handleFlagPart, isFlagging } = useFlagPartHandler({
    onOpenUnflagModal: (partId) => {
      setFlagPartId(partId);
      setIsOpenUnFlagModal(true);
    },
  });

  useQueryHandler({
    isError: isErrorParts || isErrorFirstTierSuppliers,
    isFetching:
      isLoadingFirstTierSuppliers ||
      isInsertingPart ||
      isUpdatingPart ||
      isFlagging ||
      isFetchingForDownload ||
      isImportSubmitting ||
      isImportV1Submitting,
  });

  useEffect(() => {
    document.title = "First Tier Supplier | AMPA";

    const savedParams = sessionStorage.getItem(`partManagement-params`);
    if (savedParams && searchParams.toString() === "") {
      const params = JSON.parse(savedParams);
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (searchFilter) {
        newParams.set("search", searchFilter);
      } else {
        newParams.delete("search");
      }
      // Reset to first page whenever the search term changes so the
      // pagination slice doesn't land on a stale page from before filtering,
      // which would show rows unrelated to the new search results.
      newParams.set("page", String(0));
      return newParams;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter]);

  useEffect(() => {
    if (searchParams.toString()) {
      sessionStorage.setItem(
        `partManagement-params`,
        JSON.stringify(Object.fromEntries(searchParams)),
      );
    }
  }, [searchParams]);

  useEffect(() => {
    if (firstTierSuppliers && supplierCode) {
      const foundSupplier = firstTierSuppliers.find(
        (sup) => sup.supplier_code === supplierCode,
      );
      setSupplier(foundSupplier || null);

      if (
        !foundSupplier ||
        !foundSupplier.template_type ||
        foundSupplier.template_type.toLowerCase() !== "part"
      ) {
        navigate("/breakdown");
      }
    }
  }, [supplierCode, firstTierSuppliers, navigate]);

  const { filteredParts, totalFilteredPartsCount } = useMemo(() => {
    if (parts) {
      let filtered = parts;
      if (searchFilter) {
        filtered = filtered.filter(
          (part) =>
            part.part_number
              .toLowerCase()
              .includes(searchFilter.toLowerCase()) ||
            part.part_name.toLowerCase().includes(searchFilter.toLowerCase()),
        );
      }
      const totalFilteredPartsCount = filtered.length;
      if (page !== null && rowsPerPage !== null) {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        filtered = filtered.slice(startIndex, endIndex);
      }
      return { filteredParts: filtered, totalFilteredPartsCount };
    }
    return { filteredParts: [], totalFilteredPartsCount: 0 };
  }, [parts, searchFilter, page, rowsPerPage]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedParts([]);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("active", String(newValue));
      newParams.set("page", String(0));
      return newParams;
    }, { replace: true });
  };

  const handleVersionChange = (value: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("version", value);
      newParams.set("page", String(0));
      return newParams;
    }, { replace: true });
    setSelectedParts([]);
  };

  const handleSearch = (value: string) => {
    setSearchFilter(value);
  };

  const handleSavePart = (partData: partSKP) => {
    insertPart(
      { supplierCode: supplier?.supplier_code || "", payload: partData },
      {
        onSuccess: () => {
          showToast("เพิ่ม Part สำเร็จ", "success");
          setSelectedParts([]);
        },
        onError: (error) => {
          console.error("Error inserting part:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleDeactivate = () => {
    ConfirmAlert(
      "Confirm",
      `คุณต้องการ Deactivate Part ที่เลือกใช่หรือไม่?`,
      "ใช่",
      "ยกเลิก",
    ).then((confirmed) => {
      if (confirmed) {
        updatePart(
          {
            partIds: selectedParts.map((part) => part.part_id),
            isActive: false,
          },
          {
            onSuccess: () => {
              showToast("Deactivate Part สำเร็จ", "success");
              setSelectedParts([]);
            },
            onError: (error) => {
              console.error("Error deactivating part:", error);
              ErrorAlert("Error", getAxiosErrorMessage(error));
            },
          },
        );
      }
    });
  };

  const handleActivate = () => {
    ConfirmAlert(
      "Confirm",
      `คุณต้องการ Activate Part ที่เลือกใช่หรือไม่?`,
      "ใช่",
      "ยกเลิก",
    ).then((confirmed) => {
      if (confirmed) {
        updatePart(
          {
            partIds: selectedParts.map((part) => part.part_id),
            isActive: true,
          },
          {
            onSuccess: () => {
              showToast("Activate Part สำเร็จ", "success");
              setSelectedParts([]);
            },
            onError: (error) => {
              console.error("Error activating part:", error);
              ErrorAlert("Error", getAxiosErrorMessage(error));
            },
          },
        );
      }
    });
  };

  const getDetailUrl = (partId: string | null): string | null => {
    if (!supplierCode || !partId) return null;
    return `/breakdown/${supplierCode}/${version}/${partId}`;
  };

  const handleDownloadReport = async () => {
    const confirm = await ConfirmAlert(
      "Download Report",
      "ต้องการดาวน์โหลดรายงานใช่หรือไม่?",
      "ยืนยัน",
      "ยกเลิก",
    );
    if (!confirm) return;

    setIsFetchingForDownload(true);
    try {
      await exportAllPartToExcel({
        supplierName: supplier?.supplier_name || "",
        supplierCode: supplier?.supplier_code || "",
        buyerCode: supplier?.buyer_code_list?.join(", ") || "",
        adminUnitType:
          supplier?.is_admin_pct != null
            ? supplier.is_admin_pct
              ? "Percentage"
              : "THB/pcs"
            : "-",
        profitUnitType:
          supplier?.is_profit_pct != null
            ? supplier.is_profit_pct
              ? "Percentage"
              : "THB/pcs"
            : "-",
        parts: parts || [],
      });
    } finally {
      setIsFetchingForDownload(false);
    }
  };

  const handleDownloadAllPart = async () => {
    const confirm = await ConfirmAlert(
      "Download All Part",
      "ต้องการดาวน์โหลดรายงานรวมทุก Part ใช่หรือไม่?",
      "ยืนยัน",
      "ยกเลิก",
    );
    if (!confirm) return;

    const total = parts?.length ?? 0;
    setDownloadProgress({ current: 0, total });
    try {
      await exportAllPartV1ToExcel({
        supplierCode: supplier?.supplier_code || "",
        supplierName: supplier?.supplier_name || "",
        buyerCode: supplier?.buyer_code_list?.join(",") || "",
        admin: supplier?.is_admin_pct ? "Percent" : "THB/pcs",
        profit: supplier?.is_profit_pct ? "Percent" : "THB/pcs",
        parts: parts || [],
        onProgress: (current, tot) => setDownloadProgress({ current, total: tot }),
      });
    } finally {
      setDownloadProgress(null);
    }
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb
        current={supplier?.supplier_abbr}
        previous={[{ key: "breakdown" }]}
      />
      <Box mt={1}>
        <Box
          display={"flex"}
          alignItems="center"
          justifyContent="flex-start"
          gap={2}
        >
          <BackButton to="/breakdown" />
        </Box>
        <Box mt={1} display="flex" flexDirection="row" gap={2} width="100%">
          <SupplierDetailCard
            title={supplier?.supplier_code || ""}
            text={supplier?.supplier_name || ""}
          />
          <SupplierDetailCard
            title="Buyer ID"
            text={supplier?.buyer_code_list?.join(", ") || ""}
          />
          <SupplierDetailCard
            title="Last Updated"
            text={`${formattedDateTime(supplier?.update_date || "")}`}
          />
          <SupplierDetailCard
            title="Active Parts"
            text={supplier?.active_part?.toString() || "0"}
          />
        </Box>
        <Box mt={2}>
          <TextField
            label="ค้นหา Part Number / Part Name"
            variant="outlined"
            size="small"
            value={searchFilter}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{
              width: 300,
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#cccccc",
                },
                "&:hover fieldset": {
                  borderColor: "#999999",
                },
              },
            }}
          />
          <FormControl size="small">
            <InputLabel
              id="version-label"
              sx={{
                backgroundColor: "#fff",
                px: 0.5,
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              }}
            >
              Version
            </InputLabel>
            <Select
              id="version-label"
              label="Version"
              size="small"
              value={version}
              onChange={(e) => handleVersionChange(e.target.value)}
              sx={{ ml: 2, width: 100, fontSize: 14, fontWeight: 600 }}
            >
              <MenuItem value={"v2"}>v2</MenuItem>
              <MenuItem value={"v1"}>v1</MenuItem>
            </Select>
          </FormControl>
          <Box mt={1} display="flex" justifyContent="space-between">
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              gap={1}
            >
              <Tabs
                value={activeTab}
                aria-label="part management tabs"
                onChange={handleTabChange}
              >
                <Tab label="Active" />
                <Tab label="Inactive" />
              </Tabs>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={170}
                height="80%"
                gap={0.5}
                ml={1}
                sx={{
                  color: "#0288d1",
                  fontSize: 12,
                  backgroundColor: "#e3f2fd",
                  borderRadius: 1,
                  px: 1,
                }}
              >
                <Info fontSize="small" /> Admin Unit :{" "}
                {supplier?.is_admin_pct != null
                  ? supplier.is_admin_pct
                    ? "Percentage"
                    : "THB/pcs"
                  : "-"}
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={170}
                height="80%"
                gap={0.5}
                sx={{
                  color: "#0288d1",
                  fontSize: 12,
                  backgroundColor: "#e3f2fd",
                  borderRadius: 1,
                  px: 1,
                }}
              >
                <Info fontSize="small" /> Profit Unit :{" "}
                {supplier?.is_profit_pct != null
                  ? supplier.is_profit_pct
                    ? "Percentage"
                    : "THB/pcs"
                  : "-"}
              </Box>
            </Box>
            <Box
              mt={2}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                fontSize="0.9rem"
                mr={1}
              >
                Selected {selectedParts?.length || 0} items
              </Typography>
              {version === "v1" && (
                <Button
                  variant="outlined"
                  sx={{
                    ...DefaultSecondaryButton,
                    fontSize: 10,
                    width: 180,
                  }}
                  onClick={handleDownloadAllPart}
                >
                  <InsertDriveFile /> Download All Part
                </Button>
              )}
              <Button
                variant="outlined"
                sx={{
                  ...DefaultSecondaryButton,
                  fontSize: 10,
                  width: 120,
                }}
                onClick={handleDownloadReport}
              >
                <InsertDriveFile /> Download
              </Button>
              {version === "v2" && (
                <Button
                  variant="outlined"
                  sx={{ ...DefaultSecondaryButton, fontSize: 10, width: 120 }}
                  onClick={() => {
                    setOpenDuplicatePartModal(true);
                  }}
                  disabled={selectedParts?.length === 0 || !canEdit}
                >
                  <ContentCopyIcon /> Duplicate
                </Button>
              )}
              {activeTab === 0 ? (
                <Button
                  variant="outlined"
                  sx={{
                    ...DefaultSecondaryButton,
                    fontSize: 10,
                    width: 120,
                    color: "#d32f2f",
                    borderColor: "#d32f2f",
                  }}
                  onClick={handleDeactivate}
                  disabled={selectedParts?.length === 0 || !canEdit}
                >
                  <DoNotDisturbIcon /> Deactivate
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  sx={{
                    ...DefaultSecondaryButton,
                    fontSize: 10,
                    width: 120,
                    color: "success.light",
                    borderColor: "success.light",
                  }}
                  onClick={handleActivate}
                  disabled={selectedParts?.length === 0 || !canEdit}
                >
                  <CheckIcon /> Activate
                </Button>
              )}
              {version === "v1" && canImportV1 && (
                <Button
                  variant="outlined"
                  sx={{ ...DefaultSecondaryButton, fontSize: 10, width: 120 }}
                  onClick={() => setOpenImportV1Modal(true)}
                  disabled={!canEdit}
                >
                  <Upload /> Import
                </Button>
              )}
              {version === "v2" && (
                <>
                  <Button
                    variant="outlined"
                    sx={{
                      ...DefaultSecondaryButton,
                      fontSize: 10,
                      width: 120,
                    }}
                    onClick={() => setOpenImportModal(true)}
                    disabled={!canEdit}
                  >
                    <Upload /> Import
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{
                      ...DefaultSecondaryButton,
                      fontSize: 10,
                      width: 120,
                    }}
                    onClick={() => {
                      setOpenSavePartModal(true);
                    }}
                    disabled={!canEdit}
                  >
                    <Add /> เพิ่ม Part
                  </Button>
                </>
              )}
            </Box>
          </Box>
          <PartManagementTable
            rows={filteredParts || []}
            rowCount={totalFilteredPartsCount}
            isLoading={isLoadingParts}
            page={page}
            setPage={(newPage: number) => {
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("page", String(newPage));
                return newParams;
              }, { replace: true });
            }}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(newRowsPerPage: number) => {
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("limit", String(newRowsPerPage));
                newParams.set("page", "0");
                return newParams;
              }, { replace: true });
            }}
            selectedParts={selectedParts}
            setSelectedParts={setSelectedParts}
            getDetailUrl={getDetailUrl}
            handleFlagPart={handleFlagPart}
            canEdit={canEdit}
          />
        </Box>
      </Box>

      <SavePartModal
        open={openSavePartModal}
        handleClose={() => setOpenSavePartModal(false)}
        handleSave={handleSavePart}
        supplierCode={supplier?.supplier_code || ""}
      />

      <DuplicatePartModal
        open={openDuplicatePartModal}
        handleClose={() => setOpenDuplicatePartModal(false)}
        selectedPart={selectedParts}
        setSelectedPart={setSelectedParts}
        supplierCode={supplier?.supplier_code || ""}
      />

      <ImportPartModal
        open={openImportModal}
        handleClose={() => setOpenImportModal(false)}
        onDownloadTemplate={exportPartTemplateToExcel}
        onImport={async (file) => {
          try {
            const result = await parsePartFromExcel(
              file,
              supplier?.supplier_code ?? "",
            );
            setParsedImportResult(result);
            setOpenImportModal(false);
          } catch (err) {
            ErrorAlert("Import Error", getAxiosErrorMessage(err));
          }
        }}
      />

      <ImportPreviewModal
        open={!!parsedImportResult}
        data={parsedImportResult}
        isSubmitting={isImportSubmitting}
        onClose={() => setParsedImportResult(null)}
        onConfirm={() => {
          if (!parsedImportResult) return;
          importPart(
            {
              body: parsedImportResult.body,
              supplierCode: parsedImportResult.supplierCode,
            },
            {
              onSuccess: () => {
                setParsedImportResult(null);
                showToast("นำเข้าข้อมูลสำเร็จ", "success");
              },
              onError: (err) => {
                ErrorAlert("Import Error", getAxiosErrorMessage(err));
              },
            },
          );
        }}
      />

      <ImportPartModal
        open={openImportV1Modal}
        handleClose={() => setOpenImportV1Modal(false)}
        onDownloadTemplate={exportPartV1TemplateToExcel}
        onImport={async (file) => {
          try {
            const result = await parsePartV1FromExcel(
              file,
              supplier?.supplier_code ?? "",
            );
            setParsedImportV1Result(result);
            setOpenImportV1Modal(false);
          } catch (err) {
            ErrorAlert("Import Error", getAxiosErrorMessage(err));
          }
        }}
      />

      <ImportPreviewModalV1
        open={!!parsedImportV1Result}
        data={parsedImportV1Result}
        isSubmitting={isImportV1Submitting}
        onClose={() => setParsedImportV1Result(null)}
        onConfirm={() => {
          if (!parsedImportV1Result) return;
          importPartV1(
            {
              body: parsedImportV1Result.body,
              supplierCode: parsedImportV1Result.supplierCode,
            },
            {
              onSuccess: () => {
                setParsedImportV1Result(null);
                showToast("นำเข้าข้อมูล V1 สำเร็จ", "success");
              },
              onError: (err) => {
                ErrorAlert("Import Error", getAxiosErrorMessage(err));
              },
            },
          );
        }}
      />

      <UnFlaggedModal
        open={isOpenUnFlagModal}
        handleClose={() => {
          setIsOpenUnFlagModal(false);
          setFlagPartId(null);
        }}
        selectedParts={[
          {
            partId: flagPartId ?? 0,
          },
        ]}
        requiredApprovalId={true}
      />

      <Dialog open={downloadProgress !== null} disableEscapeKeyDown>
        <DialogTitle>กำลังดาวน์โหลด...</DialogTitle>
        <DialogContent sx={{ width: 360, pb: 3 }}>
          <Box mb={1}>
            <LinearProgress
              variant="determinate"
              value={
                downloadProgress && downloadProgress.total > 0
                  ? (downloadProgress.current / downloadProgress.total) * 100
                  : 0
              }
            />
          </Box>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            กำลังประมวลผล {downloadProgress?.current ?? 0} /{" "}
            {downloadProgress?.total ?? 0} parts
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PartManagement;
